import { Controller } from "@hotwired/stimulus"

// Grille d'une étape du constructeur : glisser-déposer natif pour l'ordre, poignée droite pour la
// largeur (colonnes Bootstrap, aimantée sur col-md-N), poignée basse pour la hauteur d'un texte
// long, case « obligatoire ». Chaque geste envoie la disposition complète (ArrangeFormStep) ; le
// DOM reflète déjà le résultat, la page n'est rechargée qu'en cas de refus.
export default class extends Controller {
  static targets = ["tile", "required"]
  static values = { url: String }

  connect() {
    this.onDragOver = this.dragOver.bind(this)
    this.onDrop = this.drop.bind(this)
    this.element.addEventListener("dragover", this.onDragOver)
    this.element.addEventListener("drop", this.onDrop)
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.onDragOver)
    this.element.removeEventListener("drop", this.onDrop)
  }

  dragStart(event) {
    this.dragged = event.currentTarget.closest("[data-ref]")
    this.dragged.classList.add("fields-form-tile--dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.dragged.dataset.ref)
  }

  // `drop` efface this.dragged : s'il est encore là, le glisser a été annulé ou lâché hors de la
  // grille alors que dragOver a déjà réordonné le DOM, on revient à l'état du serveur.
  dragEnd() {
    if (!this.dragged) return
    this.dragged.classList.remove("fields-form-tile--dragging")
    this.dragged = null
    this.reload()
  }

  reload() {
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  dragOver(event) {
    if (!this.dragged) return
    event.preventDefault()
    const tile = event.target.closest("[data-ref]")
    if (!tile || tile === this.dragged) return
    const box = tile.getBoundingClientRect()
    const after = event.clientX > box.left + box.width / 2
    tile.parentNode.insertBefore(this.dragged, after ? tile.nextSibling : tile)
  }

  drop(event) {
    if (!this.dragged) return
    event.preventDefault()
    this.dragged.classList.remove("fields-form-tile--dragging")
    this.dragged = null
    this.save()
  }

  resizeStart(event) {
    event.preventDefault()
    const tile = event.currentTarget.closest("[data-ref]")
    const column = this.element.getBoundingClientRect().width / 12
    const start = { x: event.clientX, cols: Number(tile.dataset.cols) }
    this.track(
      (e) => this.setCols(tile, this.clamp(start.cols + Math.round((e.clientX - start.x) / column), 1, 12))
    )
  }

  resizeRowsStart(event) {
    event.preventDefault()
    const tile = event.currentTarget.closest("[data-ref]")
    const start = { y: event.clientY, rows: Number(tile.dataset.rows) }
    this.track(
      (e) => this.setRows(tile, this.clamp(start.rows + Math.round((e.clientY - start.y) / 28), 1, 6))
    )
  }

  // Suit le pointeur jusqu'au relâchement, puis enregistre.
  track(move) {
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      this.save()
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
  }

  setCols(tile, cols) {
    if (Number(tile.dataset.cols) === cols) return
    tile.classList.remove(`col-md-${tile.dataset.cols}`)
    tile.classList.add(`col-md-${cols}`)
    tile.dataset.cols = cols
  }

  setRows(tile, rows) {
    if (Number(tile.dataset.rows) === rows) return
    tile.dataset.rows = rows
    const textarea = tile.querySelector("textarea")
    if (textarea) textarea.rows = rows
  }

  async save() {
    const placements = this.tileTargets.map((tile) => ({
      ref: tile.dataset.ref,
      cols: Number(tile.dataset.cols),
      rows: Number(tile.dataset.rows),
      required: tile.querySelector("[data-fields--form-grid-target='required']")?.checked || false
    }))
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new FormData()
    body.append("_method", "patch")
    body.append("placements", JSON.stringify(placements))
    let ok
    try {
      const response = await fetch(this.urlValue, { method: "POST", body, headers: { "X-CSRF-Token": token } })
      ok = response.ok
    } catch {
      ok = false
    }
    if (!ok) this.reload()
  }
}
